import { useNavigate } from "react-router";
import { SCOrderListResponseType } from "@sellar-io/api-v2/types";
import {
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerNavigation,
  TabbedDrawerTab,
} from "@sellar-io/components";
import dayjs from "dayjs";
import { useSCOrder } from "../../../../../../hooks/api/v2/sellarCreditManagement/useSCOrder";
import {
  getOrderStage,
  OrderCommentList,
  OrderSummaryContainer,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../../../../components";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import { renderTaxName } from "../../../../../../utils/sellarHelpers";
import { OrderContactSummary } from "../../../../../../components/newOrderSummary/Summary/OrderContactSummary";
import { OrderPaymentSummary } from "../../../../../../components/newOrderSummary/Summary/OrderPaymentSummary";
import { OrderDocumentSummary } from "../../../../../../components/newOrderSummary/Summary/OrderDocumentSummary";
import { OrderEventListDialogItem } from "../../../../../order/modals/OrderEvents/OrderEventListDialogItem";
import { useSCConnection } from "../../../../../../hooks/api/v2/sellarCreditManagement/useSCConnection";
import { useSCOrderEventList } from "../../../../../../hooks/api/v2/sellarCreditManagement/useSCOrderEventList";
import { ORDER_DETAILS } from "../../../../../../constants/routes";

enum SellarCreditOrderDrawerTabs {
  Details,
  Documents,
  History,
}

const tabs = [
  { label: "Details", index: SellarCreditOrderDrawerTabs.Details },
  { label: "Documents", index: SellarCreditOrderDrawerTabs.Documents },
  { label: "History", index: SellarCreditOrderDrawerTabs.History },
];

export const SellarCreditOrderDrawer = ({
  active,
  setActive,
  order,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  order?: SCOrderListResponseType[number];
}) => {
  const navigate = useNavigate();

  return (
    <TabbedDrawer
      active={active}
      setActive={setActive}
      initTab={SellarCreditOrderDrawerTabs.Details}
    >
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={`${order?.Supplier.name} - Order #${order?.id}`}
          subtitle={`Placed on ${dayjs(order?.createdAt).format("DD/MM/YYYY")}`}
          customIcon={
            <ProfilePicture
              logoUrl={order?.Supplier.logoUrl}
              fallback="supplier-badge"
              size="large"
            />
          }
          onClick={
            order?.id
              ? () => navigate(ORDER_DETAILS.href({ orderId: order.id }))
              : undefined
          }
        />
        <TabbedDrawerNavigation tabs={tabs} />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={SellarCreditOrderDrawerTabs.Details}>
        {order && <DetailsContent order={order} />}
      </TabbedDrawerTab>

      <TabbedDrawerTab tab={SellarCreditOrderDrawerTabs.Documents}>
        {order && <DocumentsContent order={order} />}
      </TabbedDrawerTab>

      <TabbedDrawerTab tab={SellarCreditOrderDrawerTabs.History}>
        {order && <HistoryContent order={order} />}
      </TabbedDrawerTab>
    </TabbedDrawer>
  );
};

const DetailsContent = ({
  order,
}: {
  order: SCOrderListResponseType[number];
}) => {
  const { data, isLoading } = useSCOrder(order.id);

  const connection = useSCConnection(order.id, { enabled: !!data });

  const orderStage = () => getOrderStage(data);

  if (isLoading) return <SpinnerLegacy />;

  if (!data) return;

  return (
    <div className="flex flex-col gap-2 overflow-hidden bg-zinc-100 p-4">
      <OrderSummaryContainer
        stripeInvoiceId={data.stripeInvoiceId}
        supplierName={data.Supplier.Business.name}
        confirmedAt={data.confirmedAt}
        checkoutVersion={data.checkoutVersion}
        cancelledAt={data.cancelledAt}
        paymentDue={data.paymentDue}
        paymentType={data.paymentType}
        items={data.OrderItems.map((orderItem) => ({
          description: orderItem.Product.name ?? "",
          quantity: orderItem.quantity,
          cost: formatCurrency(orderItem.price),
          sku: orderItem.Product.sku,
          fullPrice: orderItem.fullPrice,
          price: orderItem.price,
          ProductId: orderItem.ProductId,
          ParentId: orderItem.Product.ParentId,
          promotionAppliedAt: orderItem.promotionAppliedAt,
        }))}
        priceProps={{
          delivery: formatCurrency(data.deliveryCharge, data.currency),
          isDeliveryChargeCapped: data.deliveryChargeCapped,
          subTotal: data.OrderItems
            ? formatCurrency(calculateItemTotal(data.OrderItems), data.currency)
            : "",
          discount:
            data.discountAmount > 0
              ? {
                  value: formatCurrency(data.discountAmount, data.currency),
                  calculatedTotal: formatCurrency(
                    data.discountAmount,
                    data.currency,
                  ),
                }
              : undefined,
          tax: {
            name: renderTaxName(data.currency),
            percentage: data.vatPercentage.toString(),
            total: formatCurrency(
              calculateVatTotal({ ...data, orderItems: data.OrderItems }),
              data.currency,
            ),
          },
          total: formatCurrency(data.price, data.currency),
        }}
      />
      <OrderPaymentSummary
        fulfillmentType={data.fulfillmentType}
        stage={orderStage()}
        paymentStatus={data.paymentStatus}
        paymentType={data.paymentType}
        paymentDue={data.paymentDue}
        paidAt={data.paidAt}
        stripeInvoiceId={data.stripeInvoiceId}
        orderId={data.id}
        invoiceFinalizedAt={data.invoiceFinalizedAt}
        cancelledBy={data.cancelledBy ?? ""}
        invoiceCreationStage={data.Supplier.invoiceCreationStage}
        confirmedAt={data.confirmedAt}
        dispatchedAt={data.dispatchedAt}
        deliveredAt={data.deliveredAt}
        orderPrice={data.price}
        hasPaymentLink={false}
        paymentList={[]} // NOTE: do we want a payment list to be rendered for SC orders?
      />
      <OrderContactSummary
        buyerContactSmsNumber={data.retailerContactSmsNumber}
        buyerEmail={data.Retailer.Business.email}
      />
      <OrderCommentList orderId={data.id} connection={connection.data?.[0]} />
    </div>
  );
};

const DocumentsContent = ({
  order,
}: {
  order: SCOrderListResponseType[number];
}) => {
  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden bg-zinc-100 p-4">
      <OrderDocumentSummary
        orderId={order.id}
        retailerIdOverride={order.RetailerId}
        isTwoOrder={Boolean(order.TwoOrder?.id)}
      />
    </div>
  );
};

const HistoryContent = ({
  order,
}: {
  order: SCOrderListResponseType[number];
}) => {
  const orderEventListQuery = useSCOrderEventList({
    query: [
      {
        type: "OrderId",
        modifier: "EQUALS",
        value: order.id,
      },
    ],
    sortBy: "createdAt",
    sortDir: "desc",
  });

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden bg-zinc-100 p-4">
      {orderEventListQuery.isPending ? (
        <SpinnerLegacy />
      ) : (
        <>
          {orderEventListQuery.data?.pages.map((page) => (
            <>
              {page.map((event, i) => (
                <OrderEventListDialogItem key={i} event={event} />
              ))}
            </>
          ))}
        </>
      )}
    </div>
  );
};
